import { TLBoundsCorner, Utils } from "@tldraw/core";
import type { TldrawApp } from "pages/flyer/builder/drawer/state/internal";
import { TLDR } from "pages/flyer/builder/drawer/state/TLDR";
import { FlipType, TldrawCommand } from "pages/flyer/builder/drawer/types";

export function flipShapes(app: TldrawApp, ids: string[], type: FlipType): TldrawCommand {
  const { selectedIds, currentPageId } = app;

  // Get only the selected shapes, not all shapes
  const selectedShapes = ids.map((id) => app.getShape(id));
  const boundsForShapes = selectedShapes.map((shape) => TLDR.getBounds(shape));

  const commonBounds = Utils.getCommonBounds(boundsForShapes);

  const { before, after } = TLDR.mutateShapes(
    app.state,
    ids,
    (shape) => {
      const shapeBounds = TLDR.getBounds(shape);
      const shapeUtil = TLDR.getShapeUtil(shape);

      switch (type) {
        case FlipType.Horizontal: {
          // For horizontal flip, we need to flip around the center of the shape
          const centerX = shapeBounds.minX + shapeBounds.width / 2;
          const centerY = shapeBounds.minY + shapeBounds.height / 2;

          const newShapeBounds = {
            ...shapeBounds,
            minX: centerX - shapeBounds.width / 2,
            maxX: centerX + shapeBounds.width / 2,
            minY: shapeBounds.minY,
            maxY: shapeBounds.maxY,
          };

          return shapeUtil.transform(shape, newShapeBounds, {
            type: TLBoundsCorner.TopLeft,
            scaleX: -1,
            scaleY: 1,
            initialShape: shape,
            transformOrigin: [0.5, 0.5],
          });
        }
        case FlipType.Vertical: {
          // For vertical flip, we need to flip around the center of the shape
          const centerX = shapeBounds.minX + shapeBounds.width / 2;
          const centerY = shapeBounds.minY + shapeBounds.height / 2;

          const newShapeBounds = {
            ...shapeBounds,
            minX: shapeBounds.minX,
            maxX: shapeBounds.maxX,
            minY: centerY - shapeBounds.height / 2,
            maxY: centerY + shapeBounds.height / 2,
          };

          return shapeUtil.transform(shape, newShapeBounds, {
            type: TLBoundsCorner.TopLeft,
            scaleX: 1,
            scaleY: -1,
            initialShape: shape,
            transformOrigin: [0.5, 0.5],
          });
        }
      }
    },
    currentPageId
  );

  return {
    id: "flip",
    before: {
      document: {
        pages: {
          [currentPageId]: { shapes: before },
        },
        pageStates: {
          [currentPageId]: {
            selectedIds,
          },
        },
      },
    },
    after: {
      document: {
        pages: {
          [currentPageId]: { shapes: after },
        },
        pageStates: {
          [currentPageId]: {
            selectedIds: ids,
          },
        },
      },
    },
  };
}
